import React, { useState, useRef, useEffect } from 'react';
import { Camera, FlashlightIcon, Upload, Keyboard, RotateCcw, CheckCircle, AlertTriangle, ArrowLeft } from 'lucide-react';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

// QR Scanner Component with Camera Integration
const QRCamera = ({ onScan, isActive }) => {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [hasCamera, setHasCamera] = useState(true);
  const [flashEnabled, setFlashEnabled] = useState(false);
  const streamRef = useRef(null);

  useEffect(() => {
    if (isActive && hasCamera) {
      startCamera();
    } else {
      stopCamera();
    }

    return () => stopCamera();
  }, [isActive, hasCamera]);

  const startCamera = async () => {
    try {
      const constraints = {
        video: {
          facingMode: 'environment', // Use back camera
          width: { ideal: 1280 },
          height: { ideal: 720 }
        }
      };

      const stream = await navigator.mediaDevices?.getUserMedia(constraints);
      streamRef.current = stream;
      
      if (videoRef?.current) {
        videoRef.current.srcObject = stream;
        videoRef?.current?.play();
      }
    } catch (error) {
      console.error('Error accessing camera:', error);
      setHasCamera(false);
    }
  };

  const stopCamera = () => {
    if (streamRef?.current) {
      streamRef?.current?.getTracks()?.forEach(track => track?.stop());
      streamRef.current = null;
    }
  };

  const toggleFlash = async () => {
    if (streamRef?.current) {
      const videoTrack = streamRef?.current?.getVideoTracks()?.[0];
      if (videoTrack && videoTrack?.getCapabilities()?.torch) {
        try {
          await videoTrack?.applyConstraints({
            advanced: [{ torch: !flashEnabled }]
          });
          setFlashEnabled(!flashEnabled);
        } catch (error) {
          console.error('Flash not supported:', error);
        }
      }
    }
  };

  // Simulate QR code detection (in real app, use a QR code library)
  const simulateQRDetection = () => {
    // This would typically use a library like jsQR or qr-scanner
    // For demo purposes, we'll simulate successful scans
    const demoQRCodes = [
      'PICKUP:12345-TORTAS-AHOGADAS-2024',
      'BUSINESS:cafe-de-tacuba-guadalajara',
      'PROMO:descuento-50-percent-noviembre'
    ];
    
    const randomQR = demoQRCodes?.[Math.floor(Math.random() * demoQRCodes?.length)];
    onScan(randomQR);
  };

  return (
    <div className="relative w-full h-full bg-black rounded-lg overflow-hidden">
      {hasCamera ? (
        <>
          <video
            ref={videoRef}
            className="w-full h-full object-cover"
            autoPlay
            playsInline
            muted
          />
          <canvas ref={canvasRef} className="hidden" />
          
          {/* Scanning overlay */}
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="relative">
              {/* Scanning frame */}
              <div className="w-64 h-64 border-2 border-white/50 rounded-lg relative">
                <div className="absolute top-0 left-0 w-8 h-8 border-t-4 border-l-4 border-green-400 rounded-tl-lg"></div>
                <div className="absolute top-0 right-0 w-8 h-8 border-t-4 border-r-4 border-green-400 rounded-tr-lg"></div>
                <div className="absolute bottom-0 left-0 w-8 h-8 border-b-4 border-l-4 border-green-400 rounded-bl-lg"></div>
                <div className="absolute bottom-0 right-0 w-8 h-8 border-b-4 border-r-4 border-green-400 rounded-br-lg"></div>
                
                {/* Scanning line animation */}
                <div className="absolute inset-0 overflow-hidden">
                  <div className="w-full h-1 bg-green-400 animate-pulse"></div>
                </div>
              </div>
              
              {/* Instructions */}
              <p className="text-white text-center mt-4 text-sm">
                Posiciona el código QR dentro del marco
              </p>
            </div>
          </div>

          {/* Controls overlay */}
          <div className="absolute bottom-4 left-0 right-0 flex justify-center items-center gap-4">
            <Button
              onClick={toggleFlash}
              variant="outline"
              size="sm"
              className="bg-black/50 border-white/30 text-white hover:bg-black/70"
            >
              <FlashlightIcon className={`h-4 w-4 ${flashEnabled ? 'text-yellow-400' : ''}`} />
            </Button>
            
            {/* Demo scan button */}
            <Button
              onClick={simulateQRDetection}
              className="bg-green-600 hover:bg-green-700"
            >
              <Camera className="h-4 w-4 mr-2" />
              Escanear (Demo)
            </Button>
          </div>
        </>
      ) : (
        <div className="w-full h-full flex items-center justify-center text-white">
          <div className="text-center">
            <Camera className="h-16 w-16 mx-auto mb-4 opacity-50" />
            <p className="text-lg font-medium mb-2">Cámara no disponible</p>
            <p className="text-sm opacity-75">Usa la opción de subir imagen o ingreso manual</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default function QRScannerScreen() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [scanMode, setScanMode] = useState('camera'); // 'camera', 'upload', 'manual'
  const [manualCode, setManualCode] = useState('');
  const [scanResult, setScanResult] = useState(null);
  const [scanHistory, setScanHistory] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);

  // Handle QR scan result
  const handleScan = async (qrData) => {
    if (!qrData || isProcessing) return;
    
    setIsProcessing(true);
    
    try {
      // Process different types of QR codes
      const result = await processQRCode(qrData);
      setScanResult(result);
      
      // Add to history
      const historyItem = {
        id: Date.now(),
        data: qrData,
        result: result,
        timestamp: new Date()?.toLocaleString('es-MX', { timeZone: 'America/Mexico_City' }),
        location: 'Guadalajara, Jalisco'
      };
      setScanHistory(prev => [historyItem, ...prev?.slice(0, 9)]); // Keep last 10 scans
      
    } catch (error) {
      setScanResult({
        type: 'error',
        title: 'Error al procesar código',
        message: 'No se pudo procesar el código QR escaneado',
        error: error?.message
      });
    } finally {
      setIsProcessing(false);
    }
  };

  // Process different QR code types
  const processQRCode = async (qrData) => {
    // Simulate API processing delay
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    if (qrData?.startsWith('PICKUP:')) {
      const pickupId = qrData?.replace('PICKUP:', '');
      return {
        type: 'pickup',
        title: 'Código de Recogida Válido',
        message: `Sorpresa bag #${pickupId} lista para recoger`,
        business: 'Tortas Ahogadas La Güerita',
        location: 'Av. Chapultepec 396, Guadalajara',
        actions: ['Confirmar Recogida', 'Ver Detalles']
      };
    } else if (qrData?.startsWith('BUSINESS:')) {
      const businessId = qrData?.replace('BUSINESS:', '');
      return {
        type: 'business',
        title: 'Perfil de Negocio',
        message: 'Accede al perfil del comercio',
        business: 'Café de Tacuba - Sucursal Centro',
        location: 'Av. Juárez 124, Centro Histórico, Guadalajara',
        actions: ['Ver Perfil', 'Ver Bolsas Disponibles']
      };
    } else if (qrData?.startsWith('PROMO:')) {
      const promoCode = qrData?.replace('PROMO:', '');
      return {
        type: 'promotion',
        title: 'Promoción Especial',
        message: '50% de descuento en tu próxima compra',
        business: 'Universidad Panamericana - Campus Guadalajara',
        validUntil: '30 de Noviembre, 2024',
        actions: ['Activar Promoción', 'Ver Términos']
      };
    } else {
      throw new Error('Código QR no reconocido');
    }
  };

  // Handle file upload for QR images
  const handleFileUpload = (event) => {
    const file = event?.target?.files?.[0];
    if (!file) return;

    // In a real app, you'd process the image with a QR reader library
    // For demo, simulate a successful scan
    const demoQRData = 'PICKUP:67890-TACOS-CARNAL-2024';
    handleScan(demoQRData);
  };

  // Handle manual code entry
  const handleManualSubmit = (e) => {
    e?.preventDefault();
    if (manualCode?.trim()) {
      handleScan(manualCode?.trim()?.toUpperCase());
      setManualCode('');
    }
  };

  // Render scan result
  const renderScanResult = () => {
    if (!scanResult) return null;

    const { type, title, message, business, location, actions, validUntil, error } = scanResult;
    const isError = type === 'error';

    return (
      <div className={`mt-6 p-4 rounded-lg border ${
        isError ? 'bg-red-50 border-red-200' : 'bg-green-50 border-green-200'
      }`}>
        <div className="flex items-start gap-3">
          {isError ? (
            <AlertTriangle className="h-6 w-6 text-red-600 flex-shrink-0 mt-0.5" />
          ) : (
            <CheckCircle className="h-6 w-6 text-green-600 flex-shrink-0 mt-0.5" />
          )}
          
          <div className="flex-1">
            <h3 className={`font-semibold ${isError ? 'text-red-900' : 'text-green-900'}`}>
              {title}
            </h3>
            <p className={`mt-1 text-sm ${isError ? 'text-red-700' : 'text-green-700'}`}>
              {message}
            </p>
            
            {business && (
              <p className="mt-2 font-medium text-gray-900">{business}</p>
            )}
            
            {location && (
              <p className="text-sm text-gray-600">{location}</p>
            )}
            
            {validUntil && (
              <p className="text-sm text-gray-600">Válido hasta: {validUntil}</p>
            )}
            
            {error && (
              <p className="mt-2 text-xs text-red-600">{error}</p>
            )}
            
            {actions && (
              <div className="mt-3 flex flex-wrap gap-2">
                {actions?.map((action, index) => (
                  <Button
                    key={index}
                    size="sm"
                    variant={index === 0 ? "default" : "outline"}
                    onClick={() => {
                      // Handle action - navigate to appropriate screen
                      console.log(`Action: ${action}`);
                    }}
                  >
                    {action}
                  </Button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-4 py-3">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(-1)}
            className="p-2"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-xl font-bold text-gray-900">Escáner QR</h1>
            <p className="text-sm text-gray-600">Escanea códigos para acciones rápidas</p>
          </div>
        </div>
      </div>
      <div className="p-4 space-y-6">
        {/* Scan Mode Toggle */}
        <div className="bg-white rounded-lg p-4 border border-gray-200">
          <div className="flex items-center gap-2 mb-4">
            <div className="flex bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => setScanMode('camera')}
                className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                  scanMode === 'camera' ?'bg-white text-gray-900 shadow-sm' :'text-gray-500 hover:text-gray-900'
                }`}
              >
                <Camera className="h-4 w-4 inline mr-2" />
                Cámara
              </button>
              <button
                onClick={() => setScanMode('upload')}
                className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                  scanMode === 'upload' ?'bg-white text-gray-900 shadow-sm' :'text-gray-500 hover:text-gray-900'
                }`}
              >
                <Upload className="h-4 w-4 inline mr-2" />
                Subir
              </button>
              <button
                onClick={() => setScanMode('manual')}
                className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                  scanMode === 'manual' ?'bg-white text-gray-900 shadow-sm' :'text-gray-500 hover:text-gray-900'
                }`}
              >
                <Keyboard className="h-4 w-4 inline mr-2" />
                Manual
              </button>
            </div>
          </div>

          {/* Camera Mode */}
          {scanMode === 'camera' && (
            <div className="aspect-square max-w-md mx-auto">
              <QRCamera 
                onScan={handleScan}
                isActive={scanMode === 'camera'}
              />
            </div>
          )}

          {/* Upload Mode */}
          {scanMode === 'upload' && (
            <div className="text-center">
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 hover:border-gray-400 transition-colors">
                <Upload className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-lg font-medium text-gray-900 mb-2">
                  Sube una imagen con código QR
                </p>
                <p className="text-sm text-gray-600 mb-4">
                  Selecciona una foto desde tu galería
                </p>
                <label className="cursor-pointer">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleFileUpload}
                    className="hidden"
                  />
                  <Button>
                    Seleccionar Imagen
                  </Button>
                </label>
              </div>
            </div>
          )}

          {/* Manual Mode */}
          {scanMode === 'manual' && (
            <form onSubmit={handleManualSubmit} className="space-y-4">
              <div>
                <label htmlFor="manual-code" className="block text-sm font-medium text-gray-700 mb-2">
                  Ingresa el código manualmente
                </label>
                <Input
                  id="manual-code"
                  type="text"
                  value={manualCode}
                  onChange={(e) => setManualCode(e?.target?.value)}
                  placeholder="Ej: PICKUP:12345 o BUSINESS:cafe-centro"
                  className="font-mono"
                />
              </div>
              <Button type="submit" disabled={!manualCode?.trim()}>
                Procesar Código
              </Button>
            </form>
          )}

          {/* Processing state */}
          {isProcessing && (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600 mx-auto"></div>
              <p className="mt-2 text-sm text-gray-600">Procesando código...</p>
            </div>
          )}

          {/* Scan Result */}
          {renderScanResult()}
        </div>

        {/* Recent Scans History */}
        {scanHistory?.length > 0 && (
          <div className="bg-white rounded-lg border border-gray-200">
            <div className="p-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h2 className="font-semibold text-gray-900">Escaneos Recientes</h2>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setScanHistory([])}
                >
                  <RotateCcw className="h-4 w-4 mr-2" />
                  Limpiar
                </Button>
              </div>
            </div>
            
            <div className="divide-y divide-gray-200">
              {scanHistory?.map((item) => (
                <div key={item?.id} className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <p className="font-medium text-gray-900">{item?.result?.title}</p>
                      <p className="text-sm text-gray-600 mt-1">{item?.result?.message}</p>
                      {item?.result?.business && (
                        <p className="text-sm font-medium text-gray-700 mt-1">{item?.result?.business}</p>
                      )}
                    </div>
                    <div className="text-right text-xs text-gray-500 ml-4">
                      <p>{item?.timestamp}</p>
                      <p>{item?.location}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Help Section */}
        <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
          <h3 className="font-medium text-blue-900 mb-2">Tipos de códigos QR compatibles:</h3>
          <ul className="text-sm text-blue-700 space-y-1">
            <li>• <strong>PICKUP:</strong> Confirmación de recogida de surprise bags</li>
            <li>• <strong>BUSINESS:</strong> Acceso rápido a perfiles de comercios</li>
            <li>• <strong>PROMO:</strong> Activación de promociones especiales</li>
          </ul>
        </div>
      </div>
    </div>
  );
}